import { ContextualInfoButton, ContextualInfoButtonWithText } from "@/components/buttons/ContextualInfoButton";
import { DownloadButton } from "@/components/buttons/DownloadButton";
import { getBaseThemeName, getConfig } from "@/features/branding";
import { downloadCsvFile } from "@/features/custom_downloads";
import { GeocodingHelpButton } from "@/features/geocoders";
import { HacsOmDragger, HacsOmUploadData } from "@/features/hacs";
import { useGetOrgQuery } from "@/store/services/supabase";
import { buildPortfolioExportExampleCsv, PortfolioExportExampleKey } from "@/tools/aggregate/portfolio-export/constants";
import { usePortfolioTutorialsState } from "@/tools/aggregate/portfolio-export/PortfolioTutorialContext";
import { PortfolioRegionType } from "@/tools/aggregate/portfolio-export/types";
import ArrowLeftOutlined from "@ant-design/icons/es/icons/ArrowLeftOutlined";
import SettingOutlined from "@ant-design/icons/es/icons/SettingOutlined";
import { Button, Col, Collapse, Row, Space } from "antd";
import { useTranslation } from "react-i18next";
export interface PortfolioExportUploadFormProps {
  /** Handles whenever a CSV is selected. */
  setData: (data?: HacsOmUploadData) => void;
  handleCustomise: () => void;
  handleGoBack: () => void;
  /** File data for the chosen CSV upload. */
  data?: HacsOmUploadData;
  portfolioType?: PortfolioRegionType;
  availablePortfolioTypes: string[];
  creatablePortfolioTypes: string[];
}

/**
 * Form used to upload the source file for a Portfolio Export run.
 */
export const PortfolioExportUploadForm = ({
  setData,
  handleCustomise,
  handleGoBack,
  data,
  portfolioType,
  availablePortfolioTypes,
  creatablePortfolioTypes
}: PortfolioExportUploadFormProps) => {
  const {
    newRunModalUpload
  } = usePortfolioTutorialsState();
  return <div data-sentry-component="PortfolioExportUploadForm" data-sentry-source-file="PortfolioExportUploadForm.tsx">
      {creatablePortfolioTypes.length > 1 && <>
          <Button onClick={handleGoBack} type="text" className="mb-4 mt-[-1rem] p-2">
            <Space>
              <ArrowLeftOutlined />
              Select a different portfolio type
            </Space>
          </Button>
        </>}
      <div ref={newRunModalUpload.csvUpload}>
        <HacsOmDragger data={data} onDataChange={setData} prompt="Select .csv file" data-sentry-element="HacsOmDragger" data-sentry-source-file="PortfolioExportUploadForm.tsx" />
      </div>
      <InputSpecification availablePortfolioTypes={availablePortfolioTypes} handleCustomise={handleCustomise} portfolioType={portfolioType} data-sentry-element="InputSpecification" data-sentry-source-file="PortfolioExportUploadForm.tsx" />
    </div>;
};

/**
 * Explains how the input file should be structured and provides an example.
 */
const InputSpecification = ({
  handleCustomise,
  portfolioType,
  availablePortfolioTypes
}: {
  handleCustomise: PortfolioExportUploadFormProps["handleCustomise"];
  portfolioType?: PortfolioRegionType;
  availablePortfolioTypes: string[];
}) => {
  const {
    data: org
  } = useGetOrgQuery();
  const {
    t
  } = useTranslation("tools", {
    keyPrefix: "comprehensive.portfolioInsights.newRunModal"
  });
  const {
    t: terms
  } = useTranslation("terms");
  const {
    newRunModalUpload
  } = usePortfolioTutorialsState();
  const isGlobal = portfolioType === "global";
  const isHkma = getBaseThemeName() === "hkma";
  const templateLabel = availablePortfolioTypes.length > 1 ? isGlobal ? "Standard Global" : "Standard HKSAR" : "Standard";
  const showCustomise = true;
  const downloadExample = () => {
    downloadCsvFile(buildPortfolioExportExampleCsv(["id", "address", "latitude", "longitude", "archetype", "buildYear", "floorHeight", "marketValue", "replacementCost", "mortgageRate"] as PortfolioExportExampleKey[], portfolioType), {
      filename: templateLabel + " Portfolio Upload Template.csv"
    });
  };
  const {
    portfolioExport: config
  } = getConfig();
  const minAddresses = config.largeBank.minAddresses.toLocaleString();
  const maxAddresses = config.maxAddresses.toLocaleString();
  return <>
      <p className="mt-4 text-sm">{t("upload.blurb")}</p>
      <div className="flex items-center gap-2 pt-4">
        <div>
          <DownloadButton type="primary" onClick={downloadExample} data-sentry-element="DownloadButton" data-sentry-source-file="PortfolioExportUploadForm.tsx">
            Download {templateLabel} Template
          </DownloadButton>
        </div>
        {showCustomise && <div ref={newRunModalUpload.customiseTrigger}>
            <Button type="text" className="px-2" icon={<SettingOutlined />} onClick={() => handleCustomise()}>
              Customise Template
            </Button>
          </div>}
      </div>
      <Collapse rootClassName="mt-4 bg-surface border-gray-200 [&_.ant-collapse-item]:!border-gray-200 [&_.ant-collapse-content]:!border-none" className="prose my-2 [&_.ant-collapse-header-text]:mt-auto [&_.ant-collapse-header-text]:text-sm" size="small" expandIconPlacement="end" items={[{
      key: "uploadRequirements",
      label: "Upload Requirements",
      classNames: {
        body: "!px-4",
        header: "!px-4"
      },
      children: <Row className="prose">
                <Col span={24}>
                  <div>To avoid upload errors, follow the input guidelines below:</div>
                  <ul className="mt-0 *:m-0 *:mb-1">
                    <li>
                      <span className="font-bold">Rows: </span>
                      Each row should represent a unique location or asset. You can upload{" "}
                      {org?.is_large_bank && portfolioType === "hk" && minAddresses ? `between ${minAddresses} and ${maxAddresses}` : `up to ${maxAddresses}`}{" "}
                      assets per portfolio.
                    </li>
                    <li>
                      <span className="font-bold">Column Headers: </span>
                      Only use column headers supported in the Upload Templates.
                    </li>
                    <li>
                      <span className="font-bold">User IDs: </span>
                      {terms("userSuppliedId")}s are optional but must be unique for each asset within the portfolio.
                    </li>
                    <li>
                      <span className="font-bold">Location Data: </span>
                      For best geocoding outcomes, provide either latitude/longitude coordinates, or full street-level
                      address. Business names or landmarks may lead to geocoding inaccuracies. For more information,
                      read the{" "}
                      <GeocodingHelpButton className="!text-gray-700" isLink>
                        geocoding guide
                      </GeocodingHelpButton>
                    </li>
                    <li>
                      <span className="font-bold">Archetype Selection: </span>
                      {isHkma ? <>
                          <span>
                            If defining asset archetypes, choose from the list of supported archetypes for this
                            portfolio type.
                            <ContextualInfoButton className="!inline-flex !pl-1" category="method" id="archetypesReports" size="small" drawerWidth={750} />
                          </span>
                        </> : <>
                          <span>
                            If defining asset archetypes, choose from the list of
                            <ContextualInfoButtonWithText rootClassName="!inline-flex underline !text-gray-700" id="archetypesExplained" size="small" drawerWidth={750}>
                              supported archetypes.
                            </ContextualInfoButtonWithText>
                          </span>
                        </>}
                    </li>
                  </ul>
                </Col>
              </Row>
    }]} data-sentry-element="Collapse" data-sentry-source-file="PortfolioExportUploadForm.tsx" />
    </>;
};